<!-- src/components/CreateStrategyModal.vue -->
<template>
  <div
    class="modal fade"
    id="createStrategyModal"
    tabindex="-1"
    aria-labelledby="createStrategyModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content text-start">
        <div class="modal-header custom-modal-header text-start">
          <h5 class="modal-title" id="createStrategyModalLabel">
            Create New Strategy
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body text-start">
          <form @submit.prevent="createStrategy">
            <div class="mb-3">
              <label for="strategyName" class="form-label">Strategy Name</label>
              <input
                type="text"
                class="form-control"
                id="strategyName"
                v-model="strategy.name"
                required
              />
              <!-- Red warning if name is duplicated -->
              <div v-if="isNameNotValid" class="text-danger mt-1">
                Strategy with such name already exists.
              </div>
            </div>
            <div class="mb-3">
              <label for="priceRounding" class="form-label"
                >Price Rounding</label
              >
              <select
                class="form-select"
                id="priceRounding"
                v-model="strategy.roundingTypeId"
              >
                <option value="0">To nearest cent</option>
                <option value="1">To nearest dollar</option>
                <option value="2">To nearest 99c</option>
                <option value="3">To nearest higher dollar</option>
                <option value="4">To nearest higher 99c</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label d-block">
                <i class="bi bi-calendar3"></i> <b>Price by Days in Advance</b>
              </label>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="isMultipleWindows"
                  v-model="strategy.isMultipleWindows"
                />
                <label class="form-check-label" for="isMultipleWindows">
                  Adjust the price based on number of days-out
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label d-block">
                <b>% Price by Tee Sheet Utilization</b>
              </label>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="adjustByUtilization"
                  v-model="strategy.isAdjustByUtilization"
                />
                <label class="form-check-label" for="adjustByUtilization">
                  Adjust the price as the tee sheet fills
                </label>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer text-start">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
            @click="resetForm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="createStrategy"
            :disabled="isNameNotValid || !isFormValid"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { Modal } from 'bootstrap';
import { Strategy, PriceWindow, PriceWindowUtilization } from '../api';
import eventBus, { EventType } from '../eventBus';
import { pricingApi, strategyApi } from '../apiClient';
import { useToast } from 'vue-toastification';
import { isNameNotUniqueWithinFacility } from '@/utils/strategyUtils';
import { AllDay } from '@/utils/dateUtils';

export default defineComponent({
  name: 'CreateStrategyModal',
  props: {
    facilityId: {
      type: Number,
      required: true,
    },
    existingStrategies: {
      type: Array as () => Strategy[],
      required: true,
    },
  },
  emits: ['strategy-created', 'strategy-selected'],
  setup(props, { emit }) {
    const toast = useToast();
    const strategy = ref<Strategy>({
      facilityId: props.facilityId,
      name: '',
      isActive: true,
      isMultipleWindows: true,
      isSetByDayOfWeek: false,
      showExpiredWindows: false,
      roundingTypeId: 1,
      color: '#9b59b6',
      isAdjustByUtilization: false,
    });

    const isNameNotValid = computed(() => {
      return isNameNotUniqueWithinFacility(
        props.facilityId,
        strategy.value.name ?? '',
        props.existingStrategies
      );
    });

    const isFormValid = computed(() => {
      return (
        !!strategy.value.name &&
        strategy.value.name.trim() !== '' &&
        (strategy.value.isMultipleWindows ||
          strategy.value.isAdjustByUtilization)
      );
    });

    const createStrategy = async () => {
      if (
        isNameNotValid.value ||
        !isFormValid.value ||
        (
          await strategyApi.apiStrategyExistsGet(
            props.facilityId,
            strategy.value.name || '',
            strategy.value.id || 0
          )
        ).data
      ) {
        toast.error(
          'Strategy with this name already exists or form is invalid.'
        );
        return;
      }

      try {
        // Step 0: Set the facilityId to the value passed in as a prop
        strategy.value.facilityId = props.facilityId;

        // Step 1: Create the strategy
        const createStrategyResponse = await strategyApi.apiStrategyPost(
          strategy.value
        );
        if (createStrategyResponse.data) {
          const createdStrategyId = createStrategyResponse.data.id;

          // Step 2: Create a PriceWindow object
          const priceWindow: PriceWindow = {
            strategyId: createdStrategyId,
            sortOrder: 0,
            name: 'Default',
            description: 'Default price window and strategy',
            daysIn: 0,
            daysOut: 9999,
          };

          // Call pricingPost with the PriceWindow object
          const pricingPostResponse = await pricingApi.pricingPost(priceWindow);
          const createdPriceWindowId = pricingPostResponse.data.id;

          // Step 3: Create a default PriceWindowUtilization object
          const defaultPriceWindowUtilization: PriceWindowUtilization = {
            priceWindowId: createdPriceWindowId,
            dailyUtilizations: [
              { dayOfWeek: 0, timeWindows: getDefaultTimeWindows() }, //default strategy type is -same rule to all days- which uses day 0
            ],
          };

          // Call the API to create this default PriceWindowUtilization
          await pricingApi.pricingDetailsPost(defaultPriceWindowUtilization);

          // Step 4: Emit success event and reset the form
          emit('strategy-created');
          resetForm();

          // Close the modal
          const modalElement = document.getElementById('createStrategyModal');
          if (modalElement) {
            const bootstrapModal =
              Modal.getInstance(modalElement) || new Modal(modalElement);
            bootstrapModal.hide();
          }

          // Emit an event to continue to the next step
          if (createdStrategyId) {
            emit('strategy-selected', createStrategyResponse.data);
            eventBus.emit(EventType.ShowPricingLogic, {
              strategyId: createdStrategyId,
              strategyObj: createStrategyResponse.data,
            });
          }
        }
      } catch (error) {
        console.error(
          'Error creating strategy or PriceWindowUtilization:',
          error
        );
        toast.error('Failed to create strategy. Please try again.');
      }
    };

    const resetForm = () => {
      strategy.value = {
        facilityId: props.facilityId,
        name: '',
        isActive: true,
        isMultipleWindows: true,
        isSetByDayOfWeek: false,
        showExpiredWindows: false,
        roundingTypeId: 1,
        color: '#9b59b6',
        isAdjustByUtilization: false,
      };
    };

    // Helper function to generate the default time windows
    const getDefaultTimeWindows = () => {
      return [
        {
          startTime: AllDay.startTime,
          endTime: AllDay.endTime,
          utilizationRanges: [
            {
              low: 0,
              high: 100,
              pricePercentage: 100,
              id: 0,
              usePercent: true,
            },
          ],
        },
      ];
    };

    return {
      strategy,
      createStrategy,
      isFormValid,
      resetForm,
      isNameNotValid,
    };
  },
});
</script>

<style scoped>
.modal-content {
  text-align: left;
}

.custom-modal-header {
  background-color: #405f6b;
  color: white;
}

.text-danger {
  font-size: 0.9rem;
}
</style>
